import React, { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import Form from '@components/Form';
import Button from '@components/ButtonTs';
import { EyeClosedIcon, EyeOpenIcon } from '@icons/auth';
import { Logo as LevelTravelLogo } from '@icons/crm';

import { AgentApiError } from './api';
import { AgentCredentials } from '../api';
import {
  getCurrentAgency,
  LEVEL_TRAVEL_AGENCY,
  MOSGORTUR_AGENCY,
} from '../agency/config';
import mosgorturLogo from '../agency/logos/mgt_black.png';

interface AuthPageProps {
  initialError?: string;
  onSubmit: (credentials: AgentCredentials) => Promise<void>;
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Введите корректную почту')
    .required('Введите почту'),
  password: Yup.string().required('Введите пароль'),
});

const getLoginError = (error: unknown) => {
  if (!(error instanceof AgentApiError)) {
    return 'Не удалось войти. Попробуйте ещё раз';
  }

  if (error.status === 429) return 'Слишком много попыток. Попробуйте позже';
  if (error.status === 401) return 'Неверная почта или пароль';
  if (error.status === 403) return 'Нет доступа к этому агентству';

  return 'Не удалось войти. Попробуйте ещё раз';
};

const AuthPage: React.FC<AuthPageProps> = ({ initialError = '', onSubmit }) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [submitError, setSubmitError] = useState(initialError);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const agency = getCurrentAgency() || MOSGORTUR_AGENCY;

  return (
    <Page>
      <DecorativeGlow />
      <Brand>
        {agency.id === LEVEL_TRAVEL_AGENCY.id ? (
          <LevelTravelAgencyLogo aria-label={agency.name} />
        ) : (
          <AgencyLogo src={mosgorturLogo} alt={agency.name} />
        )}
      </Brand>

      <Card>
        <Eyebrow>Кабинет турагента</Eyebrow>

        <Form<AgentCredentials>
          formik={{
            initialValues: {
              email: '',
              password: '',
            },
            validationSchema,
            onSubmit: async (values) => {
              setIsSubmitting(true);
              setSubmitError('');

              try {
                await onSubmit(values);
              } catch (error) {
                setSubmitError(getLoginError(error));
                setIsSubmitting(false);
              }
            },
          }}
        >
          <Fields>
            <Form.Input
              name="email"
              type="email"
              header="Рабочая почта"
              placeholder="name@agency.ru"
              withoutSuccessIcon
              nativeInputProps={{ autoComplete: 'username', autoFocus: true }}
            />
            <Form.Input
              name="password"
              type={isPasswordVisible ? 'text' : 'password'}
              header="Пароль"
              placeholder="Введите пароль"
              withoutSuccessIcon
              nativeInputProps={{ autoComplete: 'current-password' }}
              postfixIcon={
                isPasswordVisible ? <EyeOpenIcon /> : <EyeClosedIcon />
              }
              postfixIconProps={{
                onClick: () => setIsPasswordVisible((value) => !value),
              }}
            />
          </Fields>

          {submitError && <SubmitError>{submitError}</SubmitError>}

          <SubmitButton
            disabled={isSubmitting}
            variant="primary"
            size="flex"
            type="submit"
          >
            {isSubmitting ? 'Входим…' : 'Войти'}
          </SubmitButton>
        </Form>
      </Card>
    </Page>
  );
};

const Page = styled.main`
  align-items: center;
  background: radial-gradient(
      circle at 16% 14%,
      rgba(0, 122, 255, 0.12),
      transparent 27%
    ),
    linear-gradient(145deg, #f7faff 0%, #edf3f8 100%);
  color: #262729;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  overflow: hidden;
  padding: 48px 24px 32px;
  position: relative;
`;

const DecorativeGlow = styled.div`
  background: rgba(0, 215, 255, 0.12);
  border-radius: 50%;
  filter: blur(1px);
  height: 420px;
  position: absolute;
  right: -180px;
  top: -180px;
  width: 420px;
`;

const Brand = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 30px;
  position: relative;
`;

const AgencyLogo = styled.img`
  display: block;
  height: auto;
  max-width: 72vw;
  width: 260px;
`;

const LevelTravelAgencyLogo = styled(LevelTravelLogo)`
  display: block;
  height: 92px;
  width: 92px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid rgba(194, 197, 204, 0.65);
  border-radius: 12px;
  box-shadow: 0 22px 65px rgba(37, 67, 97, 0.12);
  max-width: 430px;
  padding: 38px 40px 34px;
  position: relative;
  width: 100%;
`;

const Eyebrow = styled.div`
  color: #007aff;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  margin-bottom: 28px;
  text-transform: uppercase;
`;

const Fields = styled.div`
  display: grid;
  gap: 18px;
`;

const SubmitButton = styled(Button)`
  border: none;
  margin-top: 24px;
  width: 100%;
`;

const SubmitError = styled.div`
  background: #fff1f0;
  border-radius: 5px;
  color: #b42318;
  font-size: 12px;
  margin-top: 16px;
  padding: 9px 11px;
`;

export default AuthPage;
